import * as PIXI from 'pixi.js';
import { Assets } from '@pixi/assets';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getImagePath } from '../Util/utils';
import { Live2DModel, config, MotionPriority } from 'pixi-live2d-display/cubism4';
import { expressionData, motionData } from '../Live2d/Live2dData';
import Live2dDetectVoiceMe from '../Live2d/Live2dDetectVoiceMe';
import { useRecoilState, useRecoilValue } from 'recoil';
import { mouthShapeAtom } from '../../State';
import DrawText from './DrawText';

window.PIXI = PIXI;

export default function Test() {
  //For Retina Display
  const deviceRatio = window.devicePixelRatio || 1;

  //Reference
  const BASE_WIDTH_Ref = useRef(450); //450
  const BASE_HEIGHT_Ref = useRef(800); // 800
  const pixiRef = useRef(null);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const animeRef = useRef(null);
  const scaleRef = useRef(1);
  const editFontRef = useRef(null);

  //State
  const viewModeRef = useRef('Playing');
  //const [viewModeState, setViewModeState] = useState('Playing'); // "Idle", "Playing", "EditText"
  const [aspectState, setAspectState] = useState('portrait'); // "portrait" or "square" or "landscape"
  const [fontState, setFontState] = useState('gothic'); // "gothic" or "mincho" or "round"
  const [fontColorState, setFontColorState] = useState('#000000');
  const [fontStyleState, setFontStyleState] = useState('f1'); //"f0", "f1", "f2", "f3", "f4", "f5", "f6"
  const [objectFitState, setObjectFitState] = useState('cover'); // "contain" or "cover"

  //Temporary Loading
  const [isLoading, setIsLoading] = useState(false);

  //Temporaly form
  const inputWidthRef = useRef(0);
  const inputFontColorRef = useRef('#000000');

  //Temporaly Mouth Movement
  const [mouthState, setMouthState] = useRecoilState(mouthShapeAtom);
  const mouthIntervalRef = useRef(null);

  useEffect(() => {
    //set animation transition duration
    config.motionFadingDuration = 500;
    config.idleMotionFadingDuration = 500;

    //Init
    initLive2dArea();

    //Temporary
    document.addEventListener('keydown', escFunction, false);
  }, []);

  useEffect(() => {
    setAspectRatio(aspectState);
  }, [aspectState]);

  useEffect(() => {
    if (viewModeRef.current == 'EditFont') {
      //if (window.funcSetViewMode !== 'undefined') {
      redrawText(viewModeRef.current);
      //}
    }
  }, [fontState, fontStyleState, fontColorState]);

  //Keyboard emote dummy
  const escFunction = useCallback((event) => {
    if (modelRef.current == null) return;
    if (event.keyCode === 49) addMotion('reset');
    if (event.keyCode === 50) addMotion('hatena');
    if (event.keyCode === 51) addMotion('cry');
    if (event.keyCode === 52) addMotion('angry');
    if (event.keyCode === 53) addMotion('kirakira');
    if (event.keyCode === 54) addMotion('gaan');
    if (event.keyCode === 55) addMotion('doya');
    if (event.keyCode === 56) addMotion('smile');
    if (event.keyCode === 57) addMotion('surprise');
    //if (event.keyCode === 0) modelRef.current.internalModel.motionManager.groups.idle = 'heart';
  }, []);

  function addMotion(motionName) {
    modelRef.current.motion(motionName, undefined, MotionPriority.FORCE);
    if (animeRef.current != null) {
      animeRef.current.motion(motionName, undefined, MotionPriority.FORCE);
    }
  }

  function setAspectRatio(aspectState) {
    if (aspectState == 'square') {
      BASE_WIDTH_Ref.current = 600;
      BASE_HEIGHT_Ref.current = 600;

      wrapRef.current.style.width = 600 + 'px';
      wrapRef.current.style.height = 600 + 'px';

      setCanvasSize(600, 600);
    }
    if (aspectState == 'portrait') {
      BASE_WIDTH_Ref.current = 450;
      BASE_HEIGHT_Ref.current = 800;

      wrapRef.current.style.width = 450 + 'px';
      wrapRef.current.style.height = 800 + 'px';
      setCanvasSize(450, 800);
    }

    if (aspectState == 'landscape') {
      BASE_WIDTH_Ref.current = 800;
      BASE_HEIGHT_Ref.current = 450;

      wrapRef.current.style.width = 800 + 'px';
      wrapRef.current.style.height = 450 + 'px';
      setCanvasSize(800, 450);
    }
  }

  async function initLive2dArea() {
    if (pixiRef.current != null) return;

    pixiRef.current = new PIXI.Application({
      width: BASE_WIDTH_Ref.current,
      height: BASE_HEIGHT_Ref.current,
      view: canvasRef.current,
      autoStart: true,
      backgroundColor: 0xe9c9ff,
      resizeTo: wrapRef.current,
      //autoResize: true,
      resolution: deviceRatio,
      autoDensity: true,
      antialias: true,
    });

    pixiRef.current.renderer.on('resize', (width, height) => {
      //if (currentHeightRef.current != height && currentWidthRef.current != width)
      //setCanvasSize(width, height);
    });

    //available to sort
    pixiRef.current.stage.sortableChildren = true;
    pixiRef.current.ticker.minFPS = 10;
    pixiRef.current.ticker.maxFPS = 30;

    //load model
    await loadLive2DModel();

    //load emote model
    await loadAnimeModel();
  }

  function setCanvasSize(width, height) {
    //calculate scale
    const scaleX = width / BASE_WIDTH_Ref.current;
    const scaleY = height / BASE_HEIGHT_Ref.current;

    //setScaleState(scaleX);
    scaleRef.current = scaleX;

    //change scale
    pixiRef.current.stage.scale.x = scaleX;
    pixiRef.current.stage.scale.y = scaleY;

    //resize canvas
    pixiRef.current.renderer.resize(width, height);

    setModelPosition('bottom');
  }

  async function loadLive2DModel() {
    let modelURL = 'https://d2wp3sepl8g4xv.cloudfront.net/live2d/boy/base.model3.json';

    //let modelURL = 'https://faceless.hiho.jp/kobayashi/base.model3.json';
    let json;
    json = await fetch(modelURL).then((res) => res.json());
    json.url = modelURL;
    json.FileReferences.Motions = motionData;

    //モーションの設定の上書き

    var motionJson = 'https://faceless.hiho.jp/kobayashi-motions/motion-list2.json';

    await fetch(motionJson, { method: 'GET' })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        json.FileReferences.Motions = data;
      });

    json.FileReferences.Expressions = expressionData;

    modelRef.current = await Live2DModel.from(json, {
      autoInteract: false,
    });

    modelRef.current.zIndex = 4;
    pixiRef.current.stage.addChild(modelRef.current);
    modelRef.current.internalModel.motionManager.groups.idle = 'Idle';

    setModelPosition('bottom');
  }

  async function loadAnimeModel() {
    //Load Live2D model for animation
    let modelURL = 'https://faceless.hiho.jp/kobayashi-anime/base.model3.json';
    let json;
    try {
      json = await fetch(modelURL).then((res) => res.json());
      json.url = modelURL;
    } catch (err) {
      console.log('エラーだよ');
      return;
    }

    var motionJson = 'https://faceless.hiho.jp/kobayashi-anime/motions/motion-list.json';

    try {
      await fetch(motionJson, { method: 'GET' })
        .then((res) => res.json())
        .then((data) => {
          json.FileReferences.Motions = data;
        })
        .catch((err) => {
          throw 'erro';
        });
    } catch (err) {
      return;
    }

    animeRef.current = await Live2DModel.from(json, {
      autoInteract: false,
    });

    animeRef.current.zIndex = 5;
    pixiRef.current.stage.addChild(animeRef.current);
    animeRef.current.internalModel.motionManager.groups.idle = 'Idle';

    setModelPosition('bottom', animeRef.current);
  }

  function setModelPosition(position) {
    var refArray = [modelRef.current, animeRef.current];

    refArray.forEach((ref) => {
      if (ref != null) {
        if (aspectState == 'square') {
          if (position == 'bottom') {
            ref.x = pixiRef.current.view.width / deviceRatio / 2 / scaleRef.current;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(1, 1);
            ref.anchor.set(0.5, 1);
          } else {
            ref.x = 0;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(0.6, 0.6);
            ref.anchor.set(0, 1);
          }
        }
        if (aspectState == 'portrait') {
          if (position == 'bottom') {
            ref.x = pixiRef.current.view.width / deviceRatio / 2 / scaleRef.current;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(1.2, 1.2);
            ref.anchor.set(0.5, 1);
          } else {
            ref.x = pixiRef.current.view.width / deviceRatio / 2 / scaleRef.current;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(0.6, 0.6);
            ref.anchor.set(0.5, 1);
          }
        }
        if (aspectState == 'landscape') {
          if (position == 'bottom') {
            ref.x = pixiRef.current.view.width / deviceRatio / 2 / scaleRef.current;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(0.8, 0.8);
            ref.anchor.set(0.5, 1);
          } else {
            ref.x = 0;
            ref.y = pixiRef.current.view.height / deviceRatio / scaleRef.current;
            ref.scale.set(0.5, 0.5);
            ref.anchor.set(0, 1);
          }
        }

        if (viewModeRef.current == 'EditFont') {
          ref.scale.set(0.8, 0.8);
        }
      }
    });
  }

  async function startPlay() {
    setIsLoading(true);
    //preload  -------------------------
    let voice1 = await preloadAudio('/studio/jenny1.mp3', 0.8);
    let voice2 = await preloadAudio('/studio/jenny2.mp3', 0.8);
    let voice3 = await preloadAudio('/studio/jenny3.mp3', 0.8);
    let voice4 = await preloadAudio('/studio/jenny4.mp3', 0.8);
    let voice5 = await preloadAudio('/studio/jenny5.mp3', 0.8);
    let voice6 = await preloadAudio('/studio/jenny6.mp3', 0.8);
    let voice7 = await preloadAudio('/studio/jenny7.mp3', 0.8);
    let voice8 = await preloadAudio('/studio/jenny8.mp3', 0.8);
    let bgm = await preloadAudio('/studio/bgm.mp3', 0.3);
    let image2 = await addImage('/studio/j5.png');
    let image3 = await addImage('/studio/j6.png');
    let image4 = await addImage('/studio/j3.png');
    let video1 = await addVideo('/studio/j10.mp4');
    let video2 = await addVideo('/studio/j11.mp4');

    setIsLoading(false);

    //Dummy mouth move start
    if (mouthIntervalRef.current != null) {
      clearInterval(mouthIntervalRef.current);
      mouthIntervalRef.current = null;
    }
    mouthIntervalRef.current = setInterval(() => {
      const randomValue = Math.random();
      setMouthState(randomValue);
    }, 100);

    //Change model animation to talking mode
    modelRef.current.internalModel.motionManager.groups.idle = 'Acting';

    //Scene1 -------------------------
    //addMotion('surprise');
    const bubble1 = DrawText(
      '今日はよろしく！',
      pixiRef.current,
      'bottom',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    bgm.play();
    voice1.play();

    await delay(1500);

    //Scene2 -------------------------
    pixiRef.current.stage.removeChild(bubble1);
    voice2.play();
    image2.visible = true;
    //addMotion('kirakira');
    const bubble2 = DrawText(
      'I love Japan',
      pixiRef.current,
      'left',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    //bubble2.zIndex = 1;
    setModelPosition('left');

    await delay(1000);

    //Scene3 -------------------------
    pixiRef.current.stage.removeChild(image2);
    pixiRef.current.stage.removeChild(bubble2);

    voice3.play();
    image3.visible = true;
    const bubble3 = DrawText(
      'because I love sushi',
      pixiRef.current,
      'left',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    //addMotion('smile');
    //bubble3.zIndex = 1;

    await delay(1000);

    //Scene4 -------------------------
    pixiRef.current.stage.removeChild(image3);
    pixiRef.current.stage.removeChild(bubble3);

    voice4.play();
    image4.visible = true;
    const bubble4 = DrawText(
      'and ramen noodles',
      pixiRef.current,
      'left',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    //bubble4.zIndex = 1;

    await delay(1000);

    //Scene5 -------------------------
    pixiRef.current.stage.removeChild(image4);
    pixiRef.current.stage.removeChild(bubble4);

    setModelPosition('bottom');
    // const scary = await addScary(scaryTexture);
    //addMotion('gaan');
    const bubble5 = DrawText(
      ',but I hate Tempura',
      pixiRef.current,
      'bottom',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    voice5.play();

    await delay(1500);

    //Scene6 -------------------------
    //pixiRef.current.stage.removeChild(scary);
    pixiRef.current.stage.removeChild(bubble5);

    setModelPosition('left');
    voice6.play();
    //const heartInterval = await addHeartEffect(heart);
    //modelRef.current.motion('hatena', undefined, MotionPriority.FORCE);
    video1.visible = true;
    const bubble6 = DrawText(
      'This is my job, working as a hair dresser. I really like this',
      pixiRef.current,
      'left',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );

    await delay(2400);

    //Scene7 -------------------------

    //clearInterval(heartInterval);
    pixiRef.current.stage.removeChild(bubble6);
    pixiRef.current.stage.removeChild(video1);

    setModelPosition('bottom');
    voice7.play();

    //addMotion('angry');
    const bubble7 = DrawText(
      'if you are interested in me,',
      pixiRef.current,
      'bottom',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );

    await delay(1300);

    //Scene8 -------------------------

    pixiRef.current.stage.removeChild(bubble7);

    video2.visible = true;

    setModelPosition('left');
    voice8.play();
    //addMotion('doya');
    const bubble8 = DrawText(
      'follow me on instagram',
      pixiRef.current,
      'left',
      fontState,
      fontColorState,
      fontStyleState,
      aspectState,
      scaleRef.current,
    );
    await delay(1500);

    pixiRef.current.stage.removeChild(video2);
    pixiRef.current.stage.removeChild(bubble8);
    stopAudio(bgm);

    //Reset
    setModelPosition('bottom');
    //Change model animation to idle mode
    modelRef.current.internalModel.motionManager.groups.idle = 'Idle';

    //stop mouse movement
    if (mouthIntervalRef.current != null) {
      clearInterval(mouthIntervalRef.current);
      mouthIntervalRef.current = null;
    }
  }

  async function preloadAudio(path, volume) {
    return new Promise((resolve, reject) => {
      const audio = new Audio(getImagePath() + path);
      audio.volume = volume; //0.15;

      audio.addEventListener(
        'canplaythrough',
        function () {
          resolve(audio);
        },
        false,
      );

      audio.addEventListener(
        'error',
        function () {
          reject(new Error('Audio preload failed')); // エラーが発生した場合、Promiseを拒否
        },
        false,
      );

      audio.load();
    });
  }

  function stopAudio(audio) {
    audio.pause();
    audio.src = '';
    audio.remove();
  }

  async function addImage(path) {
    let texture;
    let loadURL = getImagePath() + path;
    texture = await Assets.load(loadURL);
    return setImageVideo(texture);
  }

  async function addVideo(path) {
    let texture;
    try {
      texture = await loadTextureAsync(getImagePath() + path);
      texture.baseTexture.resource.source.loop = true;
      //texture.baseTexture.resource.source.play();
      console.log('Texture loaded:', texture);
    } catch (error) {
      console.error('Error loading texture:', error);
    }
    return setImageVideo(texture);
  }

  async function setImageVideo(texture) {
    let bgSprite = new PIXI.Sprite(texture);

    const canvasRatio = pixiRef.current.view.width / pixiRef.current.view.height;
    const imageRatio = bgSprite.width / bgSprite.height;

    //Cover
    if (objectFitState == 'cover') {
      if (imageRatio > canvasRatio) {
        bgSprite.height = pixiRef.current.view.height / deviceRatio / scaleRef.current;
        bgSprite.width = bgSprite.height * imageRatio;
      } else {
        bgSprite.width = pixiRef.current.view.width / deviceRatio / scaleRef.current;
        bgSprite.height = bgSprite.width / imageRatio;
      }
    }

    //Contain
    if (objectFitState == 'contain') {
      if (bgSprite.height >= bgSprite.width) {
        bgSprite.height = pixiRef.current.view.height / deviceRatio / scaleRef.current;
        bgSprite.width = bgSprite.height * imageRatio;
      } else {
        bgSprite.width = pixiRef.current.view.width / deviceRatio / scaleRef.current;
        bgSprite.height = bgSprite.width / imageRatio;
      }
    }

    bgSprite.anchor.set(0.5, 0.5);
    bgSprite.position.set(
      pixiRef.current.view.width / 2 / deviceRatio / scaleRef.current,
      pixiRef.current.view.height / 2 / deviceRatio / scaleRef.current,
    );

    // Add black background
    const graphics = new PIXI.Graphics();
    graphics.beginFill(0x000000); // 黒色
    graphics.drawRect(
      0,
      0,
      pixiRef.current.view.width * deviceRatio,
      pixiRef.current.view.height * deviceRatio,
    );
    graphics.endFill();
    const backtexture = pixiRef.current.renderer.generateTexture(graphics);
    const backsprite = new PIXI.Sprite(backtexture);

    const container = new PIXI.Container();
    container.sortableChildren = true;
    container.zIndex = 1;
    container.addChild(backsprite);
    container.addChild(bgSprite);

    pixiRef.current.stage.addChild(container);
    container.visible = false;

    return container;
  }

  async function loadTextureAsync(path) {
    return new Promise((resolve, reject) => {
      let texture = PIXI.Texture.from(path);

      if (texture.baseTexture.valid) {
        // テクスチャがすでにロードされている場合
        resolve(texture);
      } else {
        texture.baseTexture.on('loaded', () => {
          resolve(texture);
        });
        texture.baseTexture.on('error', (err) => {
          reject(err);
        });
      }
    });
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const handleAspectChange = (event) => {
    //funcSetAspectRatio(event.target.value);
  };

  const handleFontChange = (event) => {
    //funcSetFontFamily(event.target.value);
  };

  const handleFontStyleChange = (event) => {
    // funcSetFontStyle(event.target.value);
  };

  const sendFontColor = (event) => {
    event.preventDefault();
    const value = inputFontColorRef.current.value;
    //funcSetFontColor(value);
  };

  const sendCanvasSize = (event) => {
    event.preventDefault();
    const widthValue = inputWidthRef.current.value;
    // funcSetCanvasSize(widthValue);
  };

  const handleViewModeChange = (event) => {
    //funcSetViewMode(event.target.value);
  };

  /*----------------- Public Function Start --------------- */

  useEffect(() => {
    window.funcSetAspectRatio = function (aspect) {
      setAspectState(aspect);
    };

    window.funcSetFontFamily = function (value) {
      setFontState(value);
    };

    window.funcSetFontStyle = function (value) {
      setFontStyleState(value);
    };

    window.funcSetFontColor = function (color) {
      setFontColorState(color);
    };

    // コンポーネントがマウントされたときに関数を定義
    window.funcSetCanvasSize = function (widthValue) {
      funcSetCanvasSize(widthValue);
    };

    window.funcSetViewMode = function (mode, size, text = 'Sample Text') {
      funcSetViewMode(mode, size, text);
    };

    // コンポーネントがアンマウントされたときに関数を削除
    return () => {
      window.funcSetFontFamily = undefined;
      window.funcSetAspectRatio = undefined;
      window.funcSetFontStyle = undefined;
      window.funcSetFontColor = undefined;
      window.funcSetCanvasSize = undefined;
      window.funcSetViewMode = undefined;
    };
  }, []);

  function funcSetViewMode(mode, size, text = 'Sample Text') {
    redrawText(mode, text);
    viewModeRef.current = mode;
    funcSetCanvasSize(size);
    // setModelPosition('bottom');
  }

  function redrawText(mode, text = 'Sample Text') {
    //Remove existing text
    if (editFontRef.current != null) {
      pixiRef.current.stage.removeChild(editFontRef.current);
      editFontRef.current = null;
    }

    if (mode == 'EditFont') {
      //setAspectRatio('portrait');

      //Show Text
      editFontRef.current = DrawText(
        text,
        pixiRef.current,
        'center',
        fontState,
        fontColorState,
        fontStyleState,
        aspectState,
        scaleRef.current,
      );
    }
  }

  function funcSetCanvasSize(widthValue) {
    if (aspectState == 'square') {
      wrapRef.current.style.width = widthValue + 'px';
      wrapRef.current.style.height = widthValue + 'px';

      setCanvasSize(widthValue, widthValue);
    }

    if (aspectState == 'portrait') {
      wrapRef.current.style.width = widthValue + 'px';
      wrapRef.current.style.height = (16 * widthValue) / 9 + 'px';

      setCanvasSize(widthValue, (16 * widthValue) / 9);
    }

    if (aspectState == 'landscape') {
      wrapRef.current.style.width = widthValue + 'px';
      wrapRef.current.style.height = (9 * widthValue) / 16 + 'px';

      setCanvasSize(widthValue, (9 * widthValue) / 16);
    }
  }

  /*----------------- Public Function End --------------- */

  return (
    <>
      <div id="canvasWrapBox" ref={wrapRef}>
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
              opacity: 0.5,
              textAlign: 'center',
              color: 'white',
              fontSize: '30px',
              top: '0%',
              left: '0%',
              paddingTop: '280px',
            }}>
            Loading
          </div>
        )}
        <canvas ref={canvasRef} onClick={startPlay}></canvas>
        <Live2dDetectVoiceMe modelRef={modelRef} />
      </div>
    </>
  );
}
